import { getDatabase, ref } from "firebase/database";
import { getFirebaseApp, update, push, get } from "./firebaseClient";
import { scribbleLobbyRef, getScribbleLobby } from "./scribbleLobby";
import { guessStatus, guessStatusTaboo } from "./guessMatch";
import {
  advanceTabooTurn,
  isActiveGuessPhase,
  tabooWordsFromGame,
  correctGuessersFromGame,
  allTabooGuessersCorrect,
} from "./tabooGame";

function lobbyDbRef(lobbyName) {
  return scribbleLobbyRef(lobbyName);
}

function strokesRef(lobbyName) {
  const app = getFirebaseApp();
  if (!app) return null;
  const db = getDatabase(app);
  const key = String(lobbyName || "")
    .trim()
    .replace(/[.#$[\]]/g, "_");
  return ref(db, `scribble_lobbies/${key}/canvas/strokes`);
}

export function strokesToArray(strokesNode) {
  if (!strokesNode || typeof strokesNode !== "object") return [];
  return Object.entries(strokesNode)
    .map(([id, s]) => ({
      id,
      tool: s?.tool || "pen",
      color: s?.color || "#1a2b5e",
      size: Number(s?.size) || 6,
      points: Array.isArray(s?.points) ? s.points : [],
    }))
    .sort((a, b) => (a.id > b.id ? 1 : -1));
}

export function chatToArray(chatNode) {
  if (!chatNode || typeof chatNode !== "object") return [];
  return Object.entries(chatNode)
    .map(([id, row]) => ({
      id,
      userId: String(row?.userId || ""),
      name: String(row?.name || "Player"),
      text: String(row?.text || ""),
      status: row?.status || "sent",
      label: String(row?.label || ""),
      createdAt: Number(row?.createdAt) || 0,
    }))
    .sort((a, b) => a.createdAt - b.createdAt);
}

/** Time to show mid-game scoreboard on /draw after a correct guess (all clients stay on Draw). */
export const ROUND_END_SCOREBOARD_MS = 5000;

export async function selectWordAndStartTurn(lobbyName, { word, hint, timerSeconds }) {
  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const sec = Math.max(30, Number(timerSeconds) || 60);
  await update(r, {
    "game/turnPhase": "drawing",
    "game/selectedWord": String(word || "").trim(),
    "game/selectedHint": String(hint || "").trim(),
    "game/timerEndsAt": Date.now() + sec * 1000,
    "game/guessedBy": null,
    "game/guessedByName": "",
    "game/guessedPoints": 0,
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}

export async function pushCanvasStroke(lobbyName, stroke) {
  const r = strokesRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await push(r, {
    tool: stroke.tool || "pen",
    color: stroke.color || "#1a2b5e",
    size: Number(stroke.size) || 6,
    points: stroke.points || [],
    createdAt: Date.now(),
  });
}

export async function clearCanvasStrokes(lobbyName) {
  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await update(r, { "canvas/strokes": {}, updatedAt: Date.now() });
}

/** Remove the most recent stroke (undo last draw action). */
export async function popLastCanvasStroke(lobbyName) {
  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const list = strokesToArray(lobby?.canvas?.strokes);
  if (list.length === 0) return false;
  const lastId = list[list.length - 1].id;
  await update(r, {
    [`canvas/strokes/${lastId}`]: null,
    updatedAt: Date.now(),
  });
  return true;
}

export async function postChatGuess(lobbyName, { userId, name, text, answerWord, pointsEarned }) {
  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");

  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  const hostId = String(lobby?.hostUserId || "");
  const gameMode = String(lobby?.gameMode || "").toLowerCase();
  const isTaboo = gameMode === "taboo";

  if (!isActiveGuessPhase(game.turnPhase)) {
    return { ok: false, reason: "not_accepting" };
  }

  if (hostId && hostId === String(userId)) {
    return { ok: false, reason: "host_cannot_guess" };
  }

  if (isTaboo) {
    const selectedPlayerId = String(game.currentDrawerId || "");
    if (selectedPlayerId && selectedPlayerId === String(userId)) {
      return { ok: false, reason: "selected_player_cannot_guess" };
    }
    if (correctGuessersFromGame(game)[String(userId)]) {
      return { ok: false, reason: "already_guessed" };
    }
  } else if (game.guessedBy) {
    return { ok: false, reason: "not_accepting" };
  }

  const tabooWords = isTaboo ? tabooWordsFromGame(game) : [];
  const answer = game.selectedWord || answerWord || "";
  const status = isTaboo
    ? guessStatusTaboo(text, answer, tabooWords)
    : guessStatus(text, answer);

  const playerName = String(name || "Player");
  let label = "";
  if (status === "correct") {
    const pts = Math.round(Number(pointsEarned) || 0);
    label = pts > 0 ? `Amazing! +${pts}` : "Congratulations!";
  } else if (status === "close") {
    label = "You are close";
  } else if (status === "taboo") {
    label = "You guessed a taboo word";
  } else if (status === "wrong") {
    label = "Wrong";
  }

  const msgId = `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const updates = {
    [`chat/${msgId}`]: {
      userId: String(userId),
      name: playerName,
      text: String(text || "").trim(),
      status,
      label,
      createdAt: Date.now(),
    },
    updatedAt: Date.now(),
  };

  if (status === "correct") {
    const pts = Math.round(Number(pointsEarned) || 0);
    updates[`game/correctGuessers/${String(userId)}`] = {
      name: playerName,
      points: pts,
      text: String(text || "").trim(),
      at: Date.now(),
    };
    updates["game/guessedBy"] = String(userId);
    updates["game/guessedByName"] = playerName;
    updates["game/guessedPoints"] = pts;
  }

  await update(r, updates);

  let roundComplete = false;
  if (isTaboo && status === "correct") {
    const fresh = await getScribbleLobby(lobbyName);
    roundComplete = allTabooGuessersCorrect(fresh);
  }

  return { ok: true, status, roundComplete };
}

/** Show scoreboard / celebration after stages.total_score has been updated. */
export async function enterRoundEnd(lobbyName) {
  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  const gameMode = String(lobby?.gameMode || "").toLowerCase();

  if (String(game.turnPhase || "") === "round_end") {
    return;
  }

  if (gameMode === "taboo") {
    if (!isActiveGuessPhase(game.turnPhase)) return;
  } else if (!game.guessedBy) {
    return;
  }

  await update(r, {
    "game/turnPhase": "round_end",
    updatedAt: Date.now(),
  });
  await advanceTurnAfterDelay(lobbyName, ROUND_END_SCOREBOARD_MS, { fromRoundEnd: true });
}

async function advanceTurnAfterDelay(lobbyName, ms, opts = {}) {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
  await advanceTurn(lobbyName, opts);
}

export async function advanceTurn(lobbyName, opts = {}) {
  const fromRoundEnd = opts.fromRoundEnd === true;

  const r = lobbyDbRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const gameMode = String(lobby?.gameMode || "manager").toLowerCase();
  if (gameMode === "taboo") {
    await advanceTabooTurn(lobbyName, opts);
    return;
  }
  const drawers = Array.isArray(lobby?.drawers) ? lobby.drawers.map(String) : [];
  const game = lobby?.game || {};
  const tp = String(game.turnPhase || "");

  /** Avoid double-advance: host skips scoreboard timer, but guesser's delayed callback still runs later. */
  if (fromRoundEnd && tp !== "round_end") {
    return;
  }
  if (!fromRoundEnd && tp !== "drawing") {
    return;
  }

  const currentIndex = Number(game.turnIndex) || 0;
  const nextIndex = currentIndex + 1;
  const roundLimit = Math.max(
    1,
    Number(lobby?.roundCount) || (gameMode === "manager" ? drawers.length : 1)
  );

  const endGame = async () => {
    await update(r, {
      phase: "completed",
      "game/turnPhase": "ended",
      "game/timerEndsAt": null,
      updatedAt: Date.now(),
    });
  };

  if (gameMode === "anchor") {
    if (nextIndex >= roundLimit) {
      await endGame();
      return;
    }
    await update(r, {
      phase: "select_drawer",
      "game/turnIndex": nextIndex,
      "game/turnPhase": "waiting_drawer",
      "game/currentDrawerId": "",
      "game/timerEndsAt": null,
      "game/selectedWord": "",
      "game/selectedHint": "",
      "game/guessedBy": null,
      "game/guessedByName": "",
      "game/guessedPoints": 0,
      canvas: { strokes: {} },
      chat: {},
      updatedAt: Date.now(),
    });
    return;
  }

  if (nextIndex >= drawers.length) {
    await endGame();
    return;
  }

  await update(r, {
    phase: "drawing",
    "game/turnIndex": nextIndex,
    "game/currentDrawerId": drawers[nextIndex],
    "game/turnPhase": "pick_word",
    "game/timerEndsAt": null,
    "game/selectedWord": "",
    "game/selectedHint": "",
    "game/guessedBy": null,
    "game/guessedByName": "",
    "game/guessedPoints": 0,
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}

/** Pick word option(s) for a turn from the question bank (one Equity Taboo card per round). */
export function wordOptionsForTurn(questions, turnIndex, wordsPerRound = 1) {
  const rows = Array.isArray(questions) ? questions : [];
  const start = turnIndex * wordsPerRound;
  const slice = rows.slice(start, start + wordsPerRound);
  return slice.map((row, i) => {
    const taboo = [
      row?.option_one,
      row?.option_two,
      row?.option_three,
    ]
      .map((t) => String(t || "").trim())
      .filter(Boolean);
    const legacy = String(row?.option_one || row?.hint || row?.correct_answer || "").trim();
    const hint =
      taboo.length >= 3
        ? `Taboo: ${taboo.join(", ")}`
        : legacy;
    return {
      id: `w_${turnIndex}_${i}`,
      word: String(row?.question_name || row?.word || "").trim(),
      hint,
      taboo,
    };
  }).filter((w) => w.word);
}
